import ThumbIcon from './ThumbIcon'

// A space's Results tab: every participant who has taken at least one call,
// with one button per call that opens that call's write-up. People with no
// call yet stay on the People tab, and so do the actions — this tab is for
// reading. The New chip and the verdict sit beside the latest call, as they
// do on People. Archived rows stay hidden here too.

// "14 min", rounded, never "0 min" for a call that did run; null when
// ElevenLabs did not report a length.
export function callMinutes(secs) {
  if (!Number.isFinite(secs)) return null
  return `${Math.max(1, Math.round(secs / 60))} min`
}

export default function ResultsList({ space, onOpenResult }) {
  const rows = (space.staff ?? []).filter((row) => !row.archived_at && row.calls.length > 0)
  const variables = space.template_variables ?? []
  const name = (row) => `${row.first_name} ${row.last_name}`
  const callCount = rows.reduce((sum, row) => sum + row.calls.length, 0)

  const columns = ['Name', ...variables.map((v) => v.label), 'Calls']
  const gridColumns = [
    'minmax(180px, 1.2fr)',
    ...variables.map(() => 'minmax(100px, 0.8fr)'),
    'minmax(240px, 2fr)',
  ].join(' ')

  return (
    <section className="disc-section" aria-labelledby="results-title">
      <div className="disc-section__head">
        <div className="disc-section__grow">
          <h3 id="results-title" className="disc-section__title">
            Results
          </h3>
          <p className="disc-section__meta">
            {rows.length === 0
              ? 'No conversations yet'
              : `${rows.length} ${rows.length === 1 ? 'person' : 'people'} · ${callCount} ${
                  callCount === 1 ? 'call' : 'calls'
                }`}
          </p>
        </div>
      </div>

      {rows.length === 0 ? (
        <div
          className="card"
          style={{ textAlign: 'center', paddingTop: 'var(--space-xl)', paddingBottom: 'var(--space-xl)' }}
        >
          <h4 className="text-h5" style={{ marginBottom: 'var(--space-xs)' }}>
            No conversations yet
          </h4>
          <p className="text-body-md" style={{ color: 'var(--color-text-secondary)' }}>
            Each call lands here with its write-up once the staff member has talked.
          </p>
        </div>
      ) : (
        <div
          className="roster"
          role="table"
          aria-label="Results"
          style={{ '--roster-cols': gridColumns }}
        >
          <div className="roster-row roster-row--head" role="row">
            {columns.map((label) => (
              <span key={label} role="columnheader">
                {label}
              </span>
            ))}
          </div>
          {rows.map((row) => {
            // Already a timeline from the server: oldest link first, attempts
            // in order. The latest is the last one.
            const calls = row.calls
            const latest = calls[calls.length - 1]
            return (
              <div className="roster-row" role="row" key={row.participant_id}>
                <div className="roster-cell" role="cell">
                  <span className="roster-name">{name(row)}</span>
                  <span className="roster-email">{row.email}</span>
                </div>
                {variables.map((variable) => (
                  <div className="roster-cell" role="cell" key={variable.key}>
                    <span className="roster-cell__label">{variable.label}</span>
                    <span>{row.variables?.[variable.key] || '—'}</span>
                  </div>
                ))}
                <div className="roster-cell" role="cell">
                  <span className="roster-cell__label">Calls</span>
                  <span className="calls">
                    {calls.map((call, index) => {
                      const minutes = callMinutes(call.duration_secs)
                      const isLatest = call === latest
                      return (
                        <span key={`${call.link_id}-${call.attempt}`} className="calls__item">
                          <button
                            type="button"
                            className="btn btn--secondary btn--sm"
                            onClick={() => onOpenResult(call.link_id, call.attempt)}
                            title={`Open the write-up of call ${index + 1}`}
                          >
                            Call {index + 1}
                            {minutes ? ` · ${minutes}` : ''}
                          </button>
                          {isLatest && row.is_new_result && (
                            <span className="chip chip--new">New</span>
                          )}
                          {isLatest && row.rating && <RatingMark rating={row.rating} />}
                        </span>
                      )
                    })}
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}

function RatingMark({ rating }) {
  const up = rating === 'up'
  return (
    <span
      className={`rating-mark rating-mark--${up ? 'up' : 'down'}`}
      role="img"
      aria-label={up ? 'Rated thumbs up' : 'Rated thumbs down'}
      title={up ? 'Rated thumbs up' : 'Rated thumbs down'}
    >
      <ThumbIcon direction={up ? 'up' : 'down'} size={16} filled />
    </span>
  )
}
